// Configuración de la API - Actualizada para MongoDB
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Configuración del cliente HTTP
class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Obtener token del localStorage
    const token = localStorage.getItem('token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        // Intentar leer el mensaje de error del servidor
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        return {
          success: false,
          message: errorMessage,
          data: null
        };
      }

      // Intentar parsear la respuesta JSON
      try {
        const data = await response.json();
        return {
          success: true,
          message: data.message || 'Operación exitosa',
          data: data.data || data
        };
      } catch {
        // Si no es JSON válido pero la respuesta fue exitosa
        return {
          success: true,
          message: 'Operación exitosa',
          data: null
        };
      }
    } catch (error) {
      // Errores de red o de conexión
      const message = error.message || 'Error de conexión';
      return {
        success: false,
        message: message,
        data: null
      };
    }
  }

  // Métodos HTTP
  async get(endpoint, params) {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    return this.request(url, {
      method: 'GET',
    });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

// Instancia del cliente API
export const apiClient = new ApiClient(API_BASE_URL);

// API Services
export const employeeApi = {
  // Obtener todos los empleados
  getAll: (params) => 
    apiClient.get('/employees', params),

  // Obtener empleado por ID
  getById: (id) => 
    apiClient.get(`/employees/${id}`),

  // Crear empleado
  create: (data) => 
    apiClient.post('/employees', data),

  // Actualizar empleado
  update: (id, data) => 
    apiClient.put(`/employees/${id}`, data),

  // Eliminar empleado
  delete: (id) => 
    apiClient.delete(`/employees/${id}`),

  // Recalcular días acumulados
  recalculateAccumulatedDays: () => 
    apiClient.post('/employees/recalculate-days'),

  // Resetear días administrativos
  resetAdministrativeDays: () => 
    apiClient.post('/employees/reset-admin-days'),

  // Agregar días especiales
  addDays: (employeeId, data) => 
    apiClient.post(`/employees/${employeeId}/add-days`, {
      type: data.type,
      days: data.days,
      reason: data.reason
    }),
};

export const vacationRequestApi = {
  // Obtener todas las solicitudes
  getAll: (params) => 
    apiClient.get('/vacation-requests', params),

  // Obtener solicitud por ID
  getById: (id) => 
    apiClient.get(`/vacation-requests/${id}`),

  // Crear solicitud
  create: (data) => 
    apiClient.post('/vacation-requests', data),

  // Actualizar solicitud completa
  update: (id, data) => 
    apiClient.put(`/vacation-requests/${id}`, data),

  // Actualizar estado de solicitud
  updateStatus: (id, data) => 
    apiClient.patch(`/vacation-requests/${id}/status`, data),

  // Eliminar solicitud
  delete: (id) => 
    apiClient.delete(`/vacation-requests/${id}`),

  // Obtener solicitudes por empleado
  getByEmployee: (employeeId, params) => 
    apiClient.get(`/vacation-requests/employee/${employeeId}`, params),
};

export const holidayApi = {
  // Obtener todos los festivos
  getAll: (params) => 
    apiClient.get('/holidays', params),

  // Obtener festivos por rango de fechas
  getByDateRange: (startDate, endDate) => 
    apiClient.get('/holidays/range', { startDate: startDate, endDate: endDate }),

  // Crear festivo
  create: (data) => 
    apiClient.post('/holidays', data),

  // Actualizar festivo
  update: (id, data) => 
    apiClient.put(`/holidays/${id}`, data),

  // Eliminar festivo
  delete: (id) => 
    apiClient.delete(`/holidays/${id}`),

  // Calcular días hábiles
  calculateWorkingDays: (startDate, endDate) => 
    apiClient.get('/holidays/working-days', { 
      startDate: startDate, 
      endDate: endDate 
    }),

  // Generar festivos recurrentes
  generateRecurringHolidays: (year) => 
    apiClient.post('/holidays/generate-recurring', { year }),
};

// Función para verificar el estado de la API
export const checkApiHealth = () => 
  apiClient.get('/health');
